// Feature search data - searchable pages and sub-features for Global Search
// Updated with current routing URLs from imuserportal_mui_lite
import { FEATURE_IDS, isFeatureEnabled } from 'utils/supportedFeatures';
import { isHiddenForRoles, MANAGER_ROLES, READ_ONLY_MANAGER_ROLES } from 'utils/managerRoles';

const hiddenForManagers = MANAGER_ROLES;
const hiddenForReadOnlyManager = READ_ONLY_MANAGER_ROLES;

const locationPremium = { featureLevel: 'location', premiumFeature: true };
const locationFree = { featureLevel: 'location', premiumFeature: false };
const organizationFree = { featureLevel: 'organization', premiumFeature: false };
const accountFree = { featureLevel: 'account', premiumFeature: false };

/** Resolve current organization name for dynamic global-search links. */
export const getCurrentOrgNameForSearch = () => {
  try {
    const savedOrgName = typeof localStorage !== 'undefined' ? localStorage.getItem('savedOrgName') : null;
    if (savedOrgName) return savedOrgName;

    const currentOrgName = typeof localStorage !== 'undefined' ? localStorage.getItem('currentOrgName') : null;
    if (currentOrgName) return currentOrgName;

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const parts = path.split('/');
    const orgIdx = parts.indexOf('organization');
    if (orgIdx !== -1 && parts.length > orgIdx + 1) {
      try {
        return decodeURIComponent(parts[orgIdx + 1]);
      } catch (e) {
        return parts[orgIdx + 1];
      }
    }
  } catch (e) {
    // ignore storage / URL access errors
  }
  return '';
};

/** Replace `{orgName}` placeholders in feature search links at navigation time. */
export const resolveFeatureLink = (link) => {
  if (!link || !link.includes('{orgName}')) {
    return link;
  }

  const orgName = getCurrentOrgNameForSearch();
  if (!orgName) {
    return link;
  }

  return link.replace(/\{orgName\}/g, encodeURIComponent(orgName));
};

/**
 * Mirrors NavItem / NavCollapse visibility rules so global search only surfaces
 * features that would appear in the left menu.
 *
 * @param {object} item - A feature search entry
 * @param {{ featureFlagsLoading?: boolean, cloudRestLoading?: boolean, cloudRestEnabled?: boolean, userRole?: string }} gates
 * @returns {boolean}
 */
export function isSearchFeatureVisible(item, gates = {}) {
  const { featureFlagsLoading = false, cloudRestLoading = false, cloudRestEnabled = false, userRole } = gates;

  if (item?.cloudRestApiGate) {
    return !cloudRestLoading && Boolean(cloudRestEnabled);
  }

  if (isHiddenForRoles(item?.hiddenForRoles, userRole)) {
    return false;
  }

  if (item?.featureGate != null && item?.featureGate !== '') {
    return !featureFlagsLoading && isFeatureEnabled(item.featureGate);
  }

  return true;
}

/** Organization spelling variations reported in Global Search (PRJCBUGEN). */
const ORGANIZATION_SEARCH_ALIASES = new Set(['oraganization', 'oraganizations']);

/**
 * Build lowercase search terms, including plural and organization typo aliases.
 * @param {string} query
 * @returns {string[]}
 */
export const getFeatureSearchTerms = (query) => {
  const lower = typeof query === 'string' ? query.trim().toLowerCase() : '';
  if (!lower) return [];

  const terms = new Set([lower]);

  if (lower.length > 3 && lower.endsWith('s') && !lower.endsWith('ss')) {
    terms.add(lower.slice(0, -1));
  }

  const applyOrganizationAlias = (term) => {
    if (ORGANIZATION_SEARCH_ALIASES.has(term)) {
      terms.add('organization');
      terms.add('organizations');
    }
  };

  applyOrganizationAlias(lower);
  if (lower.length > 3 && lower.endsWith('s') && !lower.endsWith('ss')) {
    applyOrganizationAlias(lower.slice(0, -1));
  }

  return [...terms];
};

/** Singular/plural variants for a single query token. */
const getSingularPluralVariants = (token) => {
  const variants = new Set([token]);

  if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) {
    variants.add(token.slice(0, -1));
  } else if (token.length > 2) {
    variants.add(`${token}s`);
    if (token.endsWith('y')) {
      variants.add(`${token.slice(0, -1)}ies`);
    }
  }

  return [...variants];
};

/**
 * Split a query into lowercase tokens with singular/plural variants.
 * @param {string} query
 * @returns {string[]}
 */
export const getFeatureQueryTokens = (query) => {
  const lower = typeof query === 'string' ? query.trim().toLowerCase() : '';
  if (!lower) return [];

  const tokens = new Set();
  lower
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      getSingularPluralVariants(word).forEach((variant) => tokens.add(variant));
    });

  return [...tokens];
};

const textMatchesToken = (text, token) => {
  const lowerText = String(text).toLowerCase();
  return getSingularPluralVariants(token).some((variant) => lowerText.includes(variant));
};

/**
 * @param {{ Title?: string, localizedTitle?: string, keywords?: string[] }} feature
 * @param {string} query
 */
export const matchesFeatureSearchQuery = (feature, query) => {
  if (!query?.trim() || !feature?.Title) return false;

  // The English Title stays searchable in every language; the localized title is what the user reads.
  const searchableTexts = [
    feature.Title,
    ...(feature.localizedTitle ? [feature.localizedTitle] : []),
    ...(Array.isArray(feature.keywords) ? feature.keywords : [])
  ];

  const searchTerms = getFeatureSearchTerms(query);
  const matchesPhrase = (text) => {
    const lowerText = String(text).toLowerCase();
    return searchTerms.some((term) => lowerText.includes(term));
  };

  if (searchableTexts.some(matchesPhrase)) {
    return true;
  }

  const queryTokens = getFeatureQueryTokens(query);
  if (queryTokens.length <= 1) {
    return false;
  }

  const combinedSearchableText = searchableTexts.join(' ');
  return queryTokens.every((token) => textMatchesToken(combinedSearchableText, token));
};

const SCHEDULES_SUB_FEATURE_LINKS = {
  organizationLink: '/organization/{orgName}/scheduled',
  locationLink: '/scheduled'
};

/**
 * Schedules indexed for global search like the SSID sub-features (org-wide and location-level Schedules pages).
 */
export const SCHEDULES_SUB_FEATURE_SEARCH_CONFIG = [
  {
    featureName: 'Reboot Schedule',
    displayKeyword: 'Reboot Schedule',
    keywords: ['Reboot Schedule', 'Schedules'],
    featureGate: FEATURE_IDS.REBOOT_SCHEDULE,
    ...SCHEDULES_SUB_FEATURE_LINKS
  }
];

/**
 * Build a single global-search entry for a Schedules sub-feature.
 * Same shape as SSID sub-features: user selects organization or location in the hierarchy tree after click.
 * @param {{ featureName: string, displayKeyword?: string, keywords?: string[], featureGate?: string, organizationLink: string, locationLink: string }} config
 * @returns {object[]}
 */
export const buildSchedulesSubFeatureSearchEntries = (config) => {
  return [
    {
      Title: config.featureName,
      treePromptLabel: config.displayKeyword || config.featureName,
      link: config.organizationLink,
      featureLevel: 'location',
      premiumFeature: false,
      schedulesSubFeature: true,
      subFeatureName: config.featureName,
      organizationLink: config.organizationLink,
      locationLink: config.locationLink,
      keywords: config.keywords || [config.featureName],
      featureGate: config.featureGate
    }
  ];
};

/** Flattened Schedules sub-feature entries used by global search. */
export const schedulesSubFeatureSearchEntries = SCHEDULES_SUB_FEATURE_SEARCH_CONFIG.flatMap(buildSchedulesSubFeatureSearchEntries);

/** True when pathname is a Schedules page (org-wide or location-level). */
export const isSchedulesPage = (pathname = '') => /\/scheduled\/?$/i.test(pathname);

/**
 * True when a Schedules sub-feature is clicked while the user is already on a Schedules page.
 * @param {{ schedulesSubFeature?: boolean }} feature
 * @param {string} [pathname]
 */
export const isSchedulesScopeMatchingPage = (feature, pathname = '') => {
  if (!feature?.schedulesSubFeature) return false;

  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

  return isSchedulesPage(path);
};

export const featureSearchData = {
  featureListSearchArray: [
    // ── General navigation ──────────────────────────────────────────────
    { Title: 'MSP Dashboard', link: '/mspHome/dashboard', ...accountFree, keywords: ['Dashboard'] },
    {
      Title: 'Organization Dashboard',
      link: '/organization/{orgName}/dashboard',
      ...organizationFree,
      keywords: ['Dashboard']
    },
    { Title: 'Location Dashboard', link: '/dashboard', ...locationFree, keywords: ['Dashboard'] },
    { Title: 'Organization', link: '/mspHome/organizations', ...accountFree, keywords: ['Organizations', 'All Organizations'] },
    { Title: 'Add Organization', link: '/mspHome/organizations?mode=add', ...accountFree },
    { Title: 'Locations', link: '/mspHome/locations', ...accountFree },
    { Title: 'Add Location', link: '/mspHome/locations?mode=add', ...accountFree },
    {
      Title: 'Copy Configuration',
      link: '/mspHome/locations',
      ...accountFree,
      keywords: ['Copy Config', 'Configuration Copy']
    },
    { Title: 'MSP Devices', link: '/mspHome/devices', ...accountFree, keywords: ['Devices'] },
    {
      Title: 'Organization Devices',
      link: '/organization/{orgName}/devices',
      ...organizationFree,
      keywords: ['Devices']
    },
    { Title: 'Location Devices', link: '/locationDevice', ...locationFree, keywords: ['Devices'] },
    { Title: 'MSP Alarms', link: '/mspHome/notifications', ...accountFree, keywords: ['Alarms'] },
    {
      Title: 'Organization Alarms',
      link: '/organization/{orgName}/notifications',
      ...organizationFree,
      keywords: ['Alarms']
    },
    { Title: 'Location Alarms', link: '/notifications', ...locationFree, keywords: ['Alarms'] },
    // Hindsight sits in the nav at MSP, organization and location level on one /hindsight route that reads
    // its scope from the current context, so search opens it in place rather than prompting for a scope.
    {
      Title: 'Hindsight',
      link: '/hindsight',
      ...accountFree,
      keywords: ['Telemetry Investigation', 'Investigate'],
      featureGate: FEATURE_IDS.HINDSIGHT_ENABLED
    },
    // AIDN is a location-level nav entry (LocAidn-loc); same single-flag gate as the nav tab.
    {
      Title: 'AI Defined Network',
      link: '/aidn',
      ...locationFree,
      keywords: ['AIDN', 'AI Network'],
      featureGate: [FEATURE_IDS.AIDN_MASTER_ENABLED]
    },
    { Title: 'Add Devices', link: '/mspHome/devices?mode=add', ...accountFree },
    { Title: 'Manage Subscriptions', link: '/mspHome/administration/manage-subscriptions', ...accountFree },
    { Title: 'Map', link: '/organization/{orgName}/dashboard/map', ...organizationFree, featureGate: FEATURE_IDS.MAPS },
    { Title: 'Floor Plan', titleId: 'floorMapFloorPlan', link: '/dashboard/floor-map', ...locationFree, keywords: ['Floor Map'] },
    { Title: 'Managers', link: '/mspHome/administration', ...organizationFree },
    { Title: 'Administration', link: '/mspHome/administration', ...accountFree },
    { Title: 'Users', link: '/mspHome/administration/users', ...accountFree, hiddenForRoles: hiddenForManagers },
    { Title: 'Topology', link: '/dashboard/topology', ...locationFree },
    { Title: 'MSP Reports', titleId: 'mspReports', link: '/mspHome/administration/reports', ...accountFree, featureGate: FEATURE_IDS.REPORTS, hiddenForRoles: hiddenForReadOnlyManager, keywords: ['Reports'] },
    { Title: 'Organization Reports', titleId: 'organizationReports', link: '/organization/{orgName}/administration/reports', ...organizationFree, featureGate: FEATURE_IDS.REPORTS, hiddenForRoles: hiddenForReadOnlyManager, keywords: ['Reports'] },
    { Title: 'Policy', link: '/mspHome/administration', ...organizationFree },
    { Title: 'Organization-wide SSID', link: '/organization/{orgName}/SSID', ...organizationFree, keywords: ['Org SSID', 'Org Wide SSID'] },
    ...schedulesSubFeatureSearchEntries,
    { Title: 'Events', link: '/events', ...locationFree },
    { Title: 'Clients', link: '/clients', ...locationFree },
    { Title: 'Security Center', link: '/organization/{orgName}/security-center', ...organizationFree, keywords: ['SASE Security', 'SASE'] },
    { Title: 'Plan and Billing', link: '/mspHome/administration/manage-subscriptions', ...accountFree },
    {
      Title: 'Cloud REST API',
      link: '/mspHome/administration/cloud-rest-api',
      ...accountFree,
      cloudRestApiGate: true
    },

    // ── Wi-Fi / AP (location level) ─────────────────────────────────────
    { Title: 'WiFi and Captive Portal', link: '/Wi-Fi/SSID', ...locationPremium, keywords: ['Wi-Fi'] },
    { Title: 'Wireless SSID', link: '/Wi-Fi/SSID', ...locationPremium, keywords: ['SSID'] },
    { Title: 'Wi-Fi Schedule', link: '/Wi-Fi/schedules', ...locationPremium, keywords: ['Wi-Fi Schedules', 'WiFi Schedule', 'Schedules'] },
    { Title: 'URL filtering', link: '/Wi-Fi/Advance/URLFiltering', ...locationPremium, keywords: ['URL Filtering'] },
    { Title: 'Instant WiFi', link: '/Wi-Fi/instantWiFi', ...locationPremium, keywords: ['Instant Wi-Fi'] },
    { Title: 'Automatic Channel Allocation', link: '/Wi-Fi/instantWiFi', ...locationPremium },
    { Title: 'Automatic Transmit Power Allocation', link: '/Wi-Fi/instantWiFi', ...locationPremium },
    { Title: 'Optimize Now', link: '/Wi-Fi/instantWiFi', ...locationPremium },
    { Title: 'Fast Roaming', link: '/Wi-Fi/Advance/FastRoaming', ...locationPremium },
    { Title: 'Air Bridge Groups', link: '/Wi-Fi/LoadBalancing', ...locationPremium },
    { Title: 'Load Balancing', link: '/Wi-Fi/LoadBalancing', ...locationPremium },
    {
      Title: 'Disassociate Sticky Clients',
      link: '/Wi-Fi/LoadBalancing',
      ...locationPremium,
      keywords: ['Sticky Clients']
    },
    { Title: 'Management VLAN Settings', link: '/Wi-Fi/ManagementVLAN', ...locationPremium },
    { Title: 'Wireless settings', link: '/Wi-Fi/Advance/WirelessSettings', ...locationPremium },
    { Title: 'Network Settings', link: '/Wi-Fi/Advance/Network-Settings', ...locationPremium },
    {
      Title: 'Hardware Assisted Datapath',
      link: '/Wi-Fi/Advance/Network-Settings?setting=hardwareAssistedDatapath',
      ...locationPremium,
      keywords: ['Network Settings', 'Hardware Datapath'],
      featureGate: FEATURE_IDS.NETWORK_SETTINGS_HARDWARE_TOGGLE
    },
    {
      Title: 'Log Probing Clients to Syslog',
      link: '/Wi-Fi/Advance/Network-Settings?setting=syslogProbeClients',
      ...locationPremium,
      keywords: ['Network Settings', 'Syslog', 'Probing Clients'],
      featureGate: FEATURE_IDS.NETWORK_SETTINGS_PROBING_TOGGLE
    },
    { Title: 'IGMP Snooping', link: '/Wi-Fi/Advance/Network-Settings?setting=igmpSnoopingKey', ...locationPremium },
    { Title: 'Broadcast to Unicast', link: '/Wi-Fi/Advance/Network-Settings?setting=broadcastToUnicastKey', ...locationPremium },
    { Title: 'Energy Efficiency Mode', link: '/Wi-Fi/Advance/Network-Settings?setting=energyEfficiencyMode', ...locationPremium },
    { Title: 'ARP Proxy', link: '/Wi-Fi/Advance/Network-Settings?setting=arpProxy', ...locationPremium },
    { Title: 'Multi PSK Settings', link: '/Wi-Fi/Advance/MultiPSKSettings', ...locationPremium, keywords: ['MPSK Keys', 'MPSK'] },
    {
      Title: 'Multi PSK Settings / MPSK',
      link: '/Wi-Fi/Advance/MultiPSKSettings?tab=mpsk',
      ...locationPremium,
      keywords: ['Multi PSK Settings', 'MPSK']
    },
    {
      Title: 'Multi PSK Settings / MPSK Group',
      link: '/Wi-Fi/Advance/MultiPSKSettings?tab=group',
      ...locationPremium,
      keywords: ['Multi PSK Settings', 'MPSK Group']
    },
    { Title: 'Mesh Settings', link: '/Wi-Fi/Advance/MeshNetworkSettings', ...locationPremium },
    {
      Title: 'Traffic Policies',
      link: '/Wi-Fi/Advance/TrafficPolicies',
      ...locationPremium,
      keywords: ['Wi-Fi Advanced', 'traffic policy']
    },
    {
      Title: 'Traffic Policies / Allow Broadcast/Multicast Traffic',
      link: '/Wi-Fi/Advance/TrafficPolicies?tab=allow-broadcast',
      ...locationPremium,
      keywords: ['Traffic Policies', 'Broadcast', 'Multicast']
    },
    {
      Title: 'Traffic Policies / MAC/IP Traffic Filters',
      link: '/Wi-Fi/Advance/TrafficPolicies?tab=mac-ip-filters',
      ...locationPremium,
      keywords: ['Traffic Policies', 'MAC/IP Traffic Filters']
    },
    { Title: 'Manage Profiles', link: '/Wi-Fi/Profiles', ...locationPremium, keywords: ['Profiles'] },
    { Title: 'RF Profiles', link: '/Wi-Fi/Profiles?tab=rf', ...locationPremium, keywords: ['Profiles'] },
    { Title: 'Customer Profiles', link: '/Wi-Fi/Profiles?tab=customer', ...locationPremium, keywords: ['Profiles'] },
    { Title: 'Allow Broadcast', link: '/Wi-Fi/SSID', ...locationPremium },
    { Title: 'Walled Garden', link: '/Wi-Fi/SSID', ...locationPremium },

    // ── Mobile Hotspot / MHS (location level) ───────────────────────────
    { Title: 'MHS', link: '/MobileProfile/mobile-profiles', ...locationFree, keywords: ['Mobile Hotspot'] },
    { Title: 'Mobile Profiles', link: '/MobileProfile/mobile-profiles', ...locationFree, keywords: ['Mobile Hotspot Profiles'] },
    { Title: 'Geofence', link: '/MobileProfile/geofence', ...locationFree, keywords: ['GeoFence', 'Geo Fence'] },

    // ── Wired / Switch (location level) ─────────────────────────────────
    { Title: 'Wired VLAN', link: '/wired/VLAN', ...locationPremium },
    { Title: 'VLAN/Network setup', link: '/locationSettings/VLAN', ...locationPremium },
    { Title: 'LAG', link: '/wired/lag', ...locationPremium },
    { Title: 'Spanning Tree', link: '/wired/spanningTree', ...locationPremium },
    { Title: 'Radius Configuration', link: '/wired/radiusConfiguration', ...locationPremium, keywords: ['RADIUS Configuration'] },
    {
      Title: 'PoE Downtime Schedules',
      link: '/wired/PoESchedules',
      ...locationPremium,
      keywords: ['Wired', 'PoE Operation Schedules', 'PoE Schedules']
    },
    {
      Title: 'Wired Management VLAN',
      link: '/wired/managementVlan',
      ...locationPremium,
      featureGate: FEATURE_IDS.WIRED_MANAGEMENT_VLAN
    },
    {
      Title: 'DHCP L2 Relay',
      link: '/wired/dhcpRelay',
      ...locationPremium,
      featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
    },
    {
      Title: 'DHCP L2 Relay / Global Configuration',
      link: '/wired/dhcpRelay?tab=global-config',
      ...locationPremium,
      keywords: ['DHCP L2 Relay', 'Global Configuration'],
      featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
    },
    {
      Title: 'DHCP L2 Relay / Port Configuration',
      link: '/wired/dhcpRelay?tab=port-config',
      ...locationPremium,
      keywords: ['DHCP L2 Relay', 'Port Configuration'],
      featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
    },
    {
      Title: 'DHCP Snooping',
      link: '/wired/dhcpSnooping',
      ...locationPremium,
      featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
    },
    {
      Title: 'DHCP Snooping / Global Configuration',
      link: '/wired/dhcpSnooping?tab=global-config',
      ...locationPremium,
      keywords: ['DHCP Snooping', 'Global Configuration'],
      featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
    },
    {
      Title: 'DHCP Snooping / Port Configuration',
      link: '/wired/dhcpSnooping?tab=port-config',
      ...locationPremium,
      keywords: ['DHCP Snooping', 'Port Configuration'],
      featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
    },

    // ── Location settings ───────────────────────────────────────────────
    {
      Title: 'General Settings',
      link: '/locationSettings/general',
      ...locationPremium
    },
    { Title: 'Location Settings', link: '/locationSettings/general', ...locationPremium },
    { Title: 'Network Radius', link: '/locationSettings/radius', ...locationPremium },
    { Title: 'Routing', link: '/locationSettings/routing', ...locationPremium },
    { Title: 'SNMP', link: '/locationSettings/snmp', ...locationPremium, keywords: ['SNMP v3'] },
    {
      Title: 'Syslog Configuration',
      link: '/locationSettings/Syslog',
      ...locationPremium,
      keywords: ['Syslog', 'Location Settings']
    },
    {
      Title: 'LED Settings',
      link: '/locationSettings/LED-Settings',
      ...locationPremium,
      keywords: ['LED', 'Location Settings']
    },
    { Title: 'Configuration Backup & Restore', link: '/locationSettings/configBackup', ...locationPremium },
    { Title: 'mDNS Gateway', link: '/locationSettings/mdnsGateway', ...locationPremium },
    { Title: 'Device Groups', link: '/locationSettings/device-Groups', ...locationFree },
    { Title: 'Firmware', titleId: 'firmware', link: '/locationSettings/firmware/firmware-management', ...locationPremium },
    {
      Title: 'Schedule Upgrade',
      titleId: 'firmwareTabScheduleUpgrade',
      link: '/locationSettings/firmware/schedule-upgrade',
      ...locationPremium
    },
    {
      Title: 'Target Firmware',
      titleId: 'firmwareTabTargetFirmware',
      link: '/locationSettings/firmware/target-firmware',
      ...locationPremium
    },
    { Title: 'Firmware Update', link: '/locationSettings/firmware/firmware-management', ...locationPremium },
    {
      Title: 'Check Hotfix Firmware',
      link: '/locationSettings/firmware/target-firmware',
      ...locationPremium,
      keywords: ['Hotfix Firmware']
    },
    { Title: 'Dynamic DNS', link: '/dynamicDns', ...locationPremium },

    // ── AP device detail ────────────────────────────────────────────────
    /**{ Title: 'AP Summary', link: '/devices/AP/summary', ...devicePremium },
    { Title: 'AP Configuration', link: '/devices/AP/configuration', ...devicePremium, keywords: ['Access Point Configuration'] },
    { Title: 'Radio Channels', link: '/devices/AP/configuration', ...devicePremium, keywords: ['Radio & Channels'] },
    { Title: 'IP Settings', link: '/devices/AP/configuration', ...devicePremium, keywords: ['AP IP Settings'] },
    { Title: 'AP Clients', link: '/devices/AP/clients', ...devicePremium, keywords: ['Access Point Clients'] },
    { Title: 'Neighboring AP', link: '/devices/AP/neighboring-ap', ...devicePremium },
    { Title: 'Mesh', link: '/devices/AP/mesh', ...devicePremium, keywords: ['AP Mesh'] },
    { Title: 'AP Troubleshoot', link: '/devices/AP/troubleshoot', ...devicePremium, keywords: ['Access Point Troubleshoot', 'Speedtest'] }, **/

    // ── Switch device detail ────────────────────────────────────────────
    /**{ Title: 'Switch Summary', link: '/devices/SW/summary', ...devicePremium },
    { Title: 'Port Configuration', link: '/devices/SW/configuration', ...devicePremium, keywords: ['Switch Port Configuration'] },
    { Title: 'Switch IP Settings', link: '/devices/SW/configuration', ...devicePremium },
    { Title: 'Port Mirroring', link: '/devices/SW/configuration', ...devicePremium },
    { Title: 'Static Routes', link: '/devices/SW/configuration', ...devicePremium, keywords: ['Static Route'] },
    { Title: 'Switch Connected Clients', link: '/devices/SW/connected-clients', ...devicePremium },
    { Title: 'PoE Management', link: '/devices/SW/operations', ...devicePremium, keywords: ['PoE Budget', 'Power Management', 'PoE Port Cycling'] },
    { Title: 'Cable Test', link: '/devices/SW/troubleshooting', ...devicePremium },
    { Title: 'Switch Troubleshooting', link: '/devices/SW/troubleshooting', ...devicePremium, keywords: ['Switch Troubleshoot'] },
    { Title: 'Switch Statistics', link: '/devices/SW/summary', ...devicePremium, keywords: ['Switch Traffic'] }, **/

    // ── Professional Router (PR) device detail ──────────────────────────
    /**{ Title: 'Router Summary', link: '/devices/PR/summary', ...devicePremium, keywords: ['PR Summary'] },
    { Title: 'WAN Configuration', link: '/devices/PR/settings', ...devicePremium },
    { Title: 'QoS', link: '/devices/PR/settings', ...devicePremium, featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'PR Routing', link: '/devices/PR/settings', ...devicePremium, keywords: ['Router Routing'], featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'VLAN/DHCP Server', link: '/devices/PR/settings?tab=vlan-dhcp', ...devicePremium },
    { Title: 'VPN', link: '/devices/PR/settings', ...devicePremium, featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'IPSec VPN', link: '/devices/PR/settings', ...devicePremium, keywords: ['IPSec'], featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'OpenVPN', link: '/devices/PR/settings', ...devicePremium, featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'WireGuard VPN', link: '/devices/PR/settings', ...devicePremium, keywords: ['WireGuard'], featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'Firewall Settings', link: '/devices/PR/settings', ...devicePremium, keywords: ['Firewall'], featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'Port Forwarding', link: '/devices/PR/settings', ...devicePremium },
    { Title: 'Port Triggering', link: '/devices/PR/settings', ...devicePremium, featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'Outbound NAT', link: '/devices/PR/settings', ...devicePremium, featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'Certificate Management', link: '/devices/PR/settings', ...devicePremium, keywords: ['Certificate Authority', 'Certificate'], featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'LLDP', link: '/devices/PR/settings', ...devicePremium, featureGate: FEATURE_IDS.PRX_ADVANCED_FEATURES },
    { Title: 'Router SNMP', link: '/devices/PR/settings', ...devicePremium, keywords: ['PR SNMP'] },
    { Title: 'Router Connected Clients', link: '/devices/PR/connected-clients', ...devicePremium },
    { Title: 'Router Troubleshoot', link: '/devices/PR/troubleshoot', ...devicePremium, keywords: ['GPS Lookup'] },
    { Title: 'Traffic Statistics', link: '/devices/PR/summary', ...devicePremium, keywords: ['PR Traffic'] },**/

    // ── Orbi device detail ──────────────────────────────────────────────
    /**{ Title: 'Orbi Summary', link: '/devices/ORBI/summary', ...devicePremium, featureGate: FEATURE_IDS.ORBI_DEVICE_SUPPORT },
    { Title: 'Orbi Radio Channels', link: '/devices/ORBI/configuration', ...devicePremium, featureGate: FEATURE_IDS.ORBI_DEVICE_SUPPORT },
    { Title: 'DHCP Server', link: '/devices/ORBI/configuration', ...devicePremium, keywords: ['Orbi DHCP Server'], featureGate: FEATURE_IDS.ORBI_DEVICE_SUPPORT },
    { Title: 'Device Mode', link: '/devices/ORBI/configuration', ...devicePremium, keywords: ['Router Mode', 'AP Mode'], featureGate: FEATURE_IDS.ORBI_DEVICE_SUPPORT },
    { Title: 'Satellite Management', link: '/devices/ORBI/satellites', ...devicePremium, keywords: ['Orbi Satellites'], featureGate: FEATURE_IDS.ORBI_DEVICE_SUPPORT },
    { Title: 'Orbi Troubleshooting', link: '/devices/ORBI/troubleshoot', ...devicePremium, featureGate: FEATURE_IDS.ORBI_DEVICE_SUPPORT },**/

    // ── MHS device detail ───────────────────────────────────────────────
    /**{ Title: 'MHS Summary', link: '/devices/MHS/summary', ...locationFree },
    { Title: 'MHS Connected Clients', link: '/devices/MHS/connected-clients', ...locationFree },
    { Title: 'MHS Troubleshoot', link: '/devices/MHS/troubleshoot', ...locationFree },
    { Title: 'MHS Backup and Restore', link: '/devices/MHS/restore-and-backup', ...locationFree },**/
  ],

  accountManagementArray: [
    { Title: 'Purchase Order History', link: '/mspHome/administration/manage-subscriptions', featureLevel: 'account' },
    { Title: 'Credit Allocation', link: '/mspHome/administration/manage-subscriptions', featureLevel: 'account' },
    { Title: 'Auto Allocation', link: '/mspHome/administration/manage-subscriptions', featureLevel: 'account' },
    { Title: 'Subscriptions', link: '/mspHome/administration/manage-subscriptions', featureLevel: 'account' },
    {
      Title: 'Add Subscription Key',
      link: '/mspHome/administration/manage-subscriptions?openAddKey=1',
      featureLevel: 'account',
      keywords: ['add subscription key', 'subscription key']
    },
    {
      Title: 'Purchase Confirmation Key',
      link: '/mspHome/administration/manage-subscriptions?openAddKey=1',
      featureLevel: 'account'
    },
    { Title: 'VPN Services', link: '/mspHome/administration', featureLevel: 'account' },
    { Title: 'Monthly Usage Billing', link: '/mspHome/administration', featureLevel: 'account' },
    { Title: 'Expiry in 30 Days', link: '/mspHome/administration', featureLevel: 'account' },
    { Title: 'MSP Reports', titleId: 'mspReports', link: '/mspHome/administration/reports', featureLevel: 'account', featureGate: FEATURE_IDS.REPORTS, hiddenForRoles: hiddenForReadOnlyManager, keywords: ['Reports'] },
    { Title: 'Add Organization', link: '/mspHome/organizations?mode=add', featureLevel: 'account' },
    { Title: 'Plan and Billing', link: '/mspHome/administration/manage-subscriptions', featureLevel: 'account' },
    {
      Title: 'Cloud REST API',
      link: '/mspHome/administration/cloud-rest-api',
      featureLevel: 'account',
      cloudRestApiGate: true
    }
  ]
};

const SSID_SUB_FEATURE_LINKS = {
  organizationLink: '/organization/{orgName}/SSID',
  locationLink: '/Wi-Fi/SSID'
};

/**
 * SSID sub-features indexed for global search (org-wide and location-level SSID settings).
 */
export const SSID_SUB_FEATURE_SEARCH_CONFIG = [
  {
    featureName: 'SSID Name',
    displayKeyword: 'SSID Name',
    keywords: ['SSID Name', 'ssid name', 'ssidName', 'wireless network name'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Authentication Type',
    displayKeyword: 'Authentication',
    keywords: ['Authentication Type', 'authentication type', 'security type', 'wpa', 'wpa2', 'wpa3', 'open network'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'VLAN ID',
    displayKeyword: 'VLAN',
    keywords: ['VLAN ID', 'vlan id', 'vlan', 'management vlan', 'custom ssid vlan'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Advanced Settings',
    displayKeyword: 'Advanced Settings',
    keywords: ['Advanced Settings', 'advanced settings', 'customize ssid', 'default settings'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Enable SSID',
    displayKeyword: 'Enable SSID',
    keywords: ['Enable SSID', 'enable ssid', 'disable ssid', 'ssid enable'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Broadcasting Selection',
    displayKeyword: 'Broadcasting',
    keywords: [
      'Broadcasting Selection',
      'broadcasting selection',
      'broadcast ssid',
      "don't broadcast",
      'dont broadcast',
      'broadCastSSID',
      'broadcast',
      "don't broadcast"
    ],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Band (2.4 GHz / 5 GHz / 6 GHz)',
    displayKeyword: 'Band (2.4 GHz / 5 GHz / 6 GHz)',
    keywords: ['Band', 'band', '2.4GHz', '2.4 GHz', '5GHz', '5 GHz', '6GHz', '6 GHz', 'radio band', 'dual band'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Customer Profiles',
    displayKeyword: 'Customer Profile',
    keywords: ['Customer Profiles', 'customer profiles', 'customer profile', 'cust profile'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: '802.11w (PMF)',
    displayKeyword: 'PMF',
    keywords: ['802.11w', 'PMF', 'protected management frames', '802.11w pmf'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Multi Pre-Shared Key (MPSK)',
    displayKeyword: 'MPSK',
    keywords: ['Multi Pre-Shared Key (MPSK)', 'multi pre-shared key', 'MPSK', 'multi psk', 'pre-shared key'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Captive Portal',
    displayKeyword: 'Captive Portal',
    keywords: ['Captive Portal', 'captive portal', 'splash page', 'guest portal'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Addressing and Traffic',
    displayKeyword: 'Addressing',
    keywords: ['Addressing and Traffic', 'addressing and traffic', 'bridge mode', 'nat mode', 'traffic mode', 'addressing'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Client Isolation',
    displayKeyword: 'Client Isolation',
    keywords: ['Client Isolation', 'client isolation', 'client iso'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Band Steering',
    displayKeyword: 'Band Steering',
    keywords: ['Band Steering', 'band steering', 'band steer'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: '802.11k/v',
    displayKeyword: '802.11k/v',
    keywords: ['802.11k/v', '802.11k', '802.11v', 'kvr', 'kv roaming'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Fast Roaming (802.11r)',
    displayKeyword: '802.11r',
    keywords: ['Fast Roaming (802.11r)', 'fast roaming', '802.11r', 'fast roaming 802.11r', '802.11r fast roaming'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'SSID Schedule',
    displayKeyword: 'SSID Schedule',
    keywords: ['SSID Schedule', 'ssid schedule', 'schedule ssid', 'wifi schedule'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'MAC ACL',
    displayKeyword: 'MAC ACL',
    keywords: ['MAC ACL', 'mac acl', 'Mac ACL', 'macacl', 'mac access control'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Block Devices by OS',
    displayKeyword: 'Block OS',
    keywords: ['Block Devices by OS', 'block devices by os', 'block os', 'client os block', 'operating system block'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Rate Limiting',
    displayKeyword: 'Rate Limit',
    keywords: ['Rate Limiting', 'rate limiting', 'rate limit', 'upload rate', 'download rate'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Traffic Policies',
    displayKeyword: 'Traffic Policies',
    keywords: ['Traffic Policies', 'traffic policies', 'traffic policy', 'broadcast multicast', 'mac ip traffic filter'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'Advanced Rate Selection',
    displayKeyword: 'ARS',
    keywords: ['Advanced Rate Selection', 'advanced rate selection', 'advance rate selection', 'ARS', 'rate selection'],
    ...SSID_SUB_FEATURE_LINKS
  },
  {
    featureName: 'DHCP ACK/OFFER as Unicast',
    displayKeyword: 'DHCP',
    keywords: ['DHCP ACK/OFFER as Unicast', 'dhcp ack/offer as unicast', 'dhcp unicast', 'dhcp ack', 'dhcp offer', 'ACK/OFFER as Unicast'],
    ...SSID_SUB_FEATURE_LINKS
  }
];

/**
 * Display title for SSID sub-features in global search results.
 * SSID Name is shown without a duplicate "SSID" prefix.
 * @param {string} featureName
 */
export const formatSsidSubFeatureSearchTitle = (featureName) => {
  const name = String(featureName || '').trim();
  if (!name) return 'SSID';
  if (name === 'SSID Name' || /^SSID\s/i.test(name)) {
    return name;
  }
  return `SSID ${name}`;
};

/**
 * Build a single global-search entry for an SSID sub-feature (feature name only).
 * User selects organization or location in the hierarchy tree after click.
 * @param {{ featureName: string, displayKeyword?: string, keywords?: string[], organizationLink: string, locationLink: string }} config
 * @returns {object[]}
 */
export const buildSsidSubFeatureSearchEntries = (config) => {
  return [
    {
      Title: formatSsidSubFeatureSearchTitle(config.featureName),
      treePromptLabel: config.displayKeyword || config.featureName,
      link: config.organizationLink,
      featureLevel: 'location',
      premiumFeature: false,
      ssidSubFeature: true,
      subFeatureName: config.featureName,
      organizationLink: config.organizationLink,
      locationLink: config.locationLink,
      keywords: config.keywords || [config.featureName]
    }
  ];
};

/** Flattened SSID sub-feature entries used by global search. */
export const ssidSubFeatureSearchEntries = SSID_SUB_FEATURE_SEARCH_CONFIG.flatMap(buildSsidSubFeatureSearchEntries);

/** True when pathname is the org-wide SSID page. */
export const isOrgWideSsidPage = (pathname = '') => pathname.includes('/organization/') && /\/SSID\/?$/i.test(pathname);

/** True when pathname is the location-level SSID page. */
export const isLocationSsidPage = (pathname = '') => /\/Wi-Fi\/SSID\/?$/i.test(pathname);

/** True when pathname is any SSID management page (org-wide, location, or device-group). */
export const isAnySsidPage = (pathname = '') => {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  return /\/SSID\/?$/i.test(path) || path.includes('/SSID');
};

/**
 * True when a location-level SSID row is editable (org-wide SSIDs are read-only at location level).
 * Mirrors SSID.jsx grid rules: isRowSelectable / isOrgWideDisabled.
 * @param {{ isOrgWide?: string|number }} ssidData
 */
export const isLocationEditableSsid = (ssidData) => ssidData?.isOrgWide != '1';

/**
 * True when an SSID sub-feature scope matches the SSID page the user is already on.
 * @param {{ ssidSubFeature?: boolean }} feature
 * @param {string} [pathname]
 */
export const isSsidScopeMatchingPage = (feature, pathname = '') => {
  if (!feature?.ssidSubFeature) return false;

  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

  return isAnySsidPage(path);
};

/** True when the user is scoped to a location or device group (inside a location). */
export const isAtLocationScope = () => {
  try {
    const scopeId = typeof localStorage !== 'undefined' ? localStorage.getItem('scopeId') : null;
    if (scopeId === '2' || scopeId === '3') return true;

    const selectedTypeRaw = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedType') : null;
    if (!selectedTypeRaw) return false;

    const selectedType = JSON.parse(selectedTypeRaw);
    return selectedType?.type === 'Loc' || selectedType?.type === 'CustomGroup';
  } catch {
    return false;
  }
};

/** True when the user is scoped to an organization. */
export const isAtOrganizationScope = (pathname = '') => {
  try {
    const scopeId = typeof localStorage !== 'undefined' ? localStorage.getItem('scopeId') : null;
    if (scopeId === '1') return true;

    const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
    return path.includes('/organization/');
  } catch {
    return false;
  }
};

/**
 * True when the current organization has one or more locations in the hierarchy payload.
 * Defaults to true when the org cannot be resolved so the hierarchy picker remains the safe fallback.
 * @param {Array<{ orgId?: string|number, id?: string|number, locations?: unknown[], networks?: unknown[] }>} organizations
 * @param {string|number} [orgId]
 */
export const currentOrgHasLocationsFromHierarchy = (organizations = [], orgId = '') => {
  const normalizedOrgId = String(orgId || (typeof localStorage !== 'undefined' ? localStorage.getItem('OrgId') : '') || '');
  if (!normalizedOrgId) return true;

  const currentOrg = organizations.find((org) => String(org.orgId || org.id) === normalizedOrgId);
  if (!currentOrg) return true;

  const locations = currentOrg.locations || currentOrg.networks || [];
  return Array.isArray(locations) && locations.length > 0;
};

/** Max feature matches returned before prompting the user to refine search. */
export const FEATURE_SEARCH_MAX_RESULTS = 25;

/**
 * Filter configured global-search features for a query and visibility gates.
 * @param {string} query
 * @param {{ featureFlagsLoading?: boolean, cloudRestLoading?: boolean, cloudRestEnabled?: boolean, userRole?: string }} gates
 * @param {object} [intl] - react-intl object; entries with a `titleId` gain a `localizedTitle` for matching
 *   and display. `Title` stays English: it is the entry's identity (dedupe, history, hotfix gate).
 * @returns {object[]}
 */
export function filterFeatureSearchResults(query, gates = {}, intl) {
  if (!query?.trim()) return [];

  const mergedFeatureList = [
    ...(featureSearchData.featureListSearchArray || []),
    ...(featureSearchData.accountManagementArray || []),
    ...ssidSubFeatureSearchEntries
  ];

  const searchWord = query.trim();
  // Localize and match in one pass; only the few entries with a titleId are copied.
  let filteredFeatures = mergedFeatureList.flatMap((entry) => {
    const feature =
      entry.titleId && intl ? { ...entry, localizedTitle: intl.formatMessage({ id: entry.titleId, defaultMessage: entry.Title }) } : entry;
    return matchesFeatureSearchQuery(feature, searchWord) ? [feature] : [];
  });
  filteredFeatures = filteredFeatures.filter((feature, index, array) => {
    const key = `${feature.Title}|${feature.link}|${feature.featureLevel}`;
    return index === array.findIndex((item) => `${item.Title}|${item.link}|${item.featureLevel}` === key);
  });

  if (!localStorage.getItem('checkTestFirmware') || localStorage.getItem('checkTestFirmware') === '0') {
    filteredFeatures = filteredFeatures.filter((item) => item.Title !== 'Check Hotfix Firmware');
  }

  return filteredFeatures
    .filter((item) => isSearchFeatureVisible(item, gates))
    .map(({ showWarning: _showWarning, warningType: _warningType, ...item }) => ({ ...item }));
}

export default featureSearchData;
